// PROPOSE PAYOUT ADDRESS (UI rebuild batch 4 · custody setup · reference build) — Astra's focused form: Payee, Network,
// Address, Label; "Review proposal" turns into the readback and its one confirm; the receipt names the next stage without
// implying the address is usable: the OWNER approves it in the Books, then the 10 USDT test, then his Received.
//
// Kept from screens/custody/Whitelist.tsx ProposeForm: the customers read (['custody','customers']) with a merchant's
// mark, proposeWhitelist({ partySlug, chain: 'TRC20', address, label }), the TRON shape check (34 characters, T…) with
// its two lines, the list's refresh. Dropped: "accepted only from the desk's allowed IP" — the Cloudflare device check
// replaced the IP rule (the operator, 22 Oct 2026).
import { SigninQuiet } from '../../../components/v2/custody/SigninLine';
import { isCustodySignin } from '../../../lib/errors';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { custody as api } from '../../../lib/api';
import { Field } from '../../../components/Field';
import { StateLine, ServerLine, Fact } from '../../../components/v2/kit';
import { FormFrame, FormFoot, FormAside, PRIMARY, QUIET } from '../../../components/v2/move/FormFrame';
import { CA } from '../../../components/v2/custody-admin/paths';

const textOf = (err: unknown): string => String((err as { message?: unknown } | null)?.message ?? err);
/** A TRON address is 34 characters and starts with T; the server checks the checksum. */
const TRON_SHAPE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/;

export function ProposeAddressV2() {
  const qc = useQueryClient();
  const customers = useQuery({ queryKey: ['custody', 'customers'], queryFn: api.customers });
  const [partySlug, setPartySlug] = useState('');
  const [address, setAddress] = useState('');
  const [label, setLabel] = useState('');
  const [reviewing, setReviewing] = useState(false);
  useEffect(() => { setReviewing(false); }, [partySlug, address, label]);
  // What is posted is a SNAPSHOT taken at the press, and the receipt names that snapshot (m.variables) — never the form,
  // which could be changed while the request was in flight (Screens and Astra, 27 Oct 2026, #5: posted alice, receipt
  // said Bob). The fields are locked while it is pending.
  const m = useMutation({
    mutationFn: (sent: { partySlug: string; payeeName: string; address: string; label: string }) =>
      api.proposeWhitelist({ partySlug: sent.partySlug, chain: 'TRC20', address: sent.address, label: sent.label || undefined }),
    onSuccess: () => { void qc.invalidateQueries({ queryKey: ['custody', 'whitelist'] }); },
  });
  const trimmed = address.trim();
  const shapeOk = trimmed === '' || TRON_SHAPE.test(trimmed);
  const payee = (customers.data?.customers ?? []).find((c) => c.slug === partySlug) ?? null;
  const ready = !!payee && trimmed !== '' && shapeOk;

  const aside = (
    <FormAside
      title="After proposing"
      figure="Not payable yet"
      sub="Owner approves → 10 USDT test → Received."
      help={<p>The owner approves it in the Books (Approve payout address). Then a 10 USDT test goes to it; once the customer confirms it arrived, you press Received and it is whitelisted. The test counts toward the payout. Every payout is still signed on the Nano.</p>}
    />
  );

  if (m.isSuccess) {
    return (
      <FormFrame title="Propose payout address" aside={aside}>
        <StateLine tone="good">Proposed — awaiting the owner&rsquo;s approval.</StateLine>
        <div className="border-t border-stroke-soft">
          <Fact label="Payee">{m.variables.payeeName}</Fact>
          <Fact label="Address"><span className="break-all font-mono">{m.variables.address}</span></Fact>
          <Fact label="Next">Owner approves in the Books</Fact>
        </div>
        <FormFoot note="Nothing can be paid to it yet">
          <button type="button" className={QUIET} onClick={() => { m.reset(); setAddress(''); setLabel(''); setPartySlug(''); }}>Propose another</button>
          <Link to={`${CA.payouts}?payout=${m.data.id}`} className={QUIET}>Open it</Link>
        </FormFoot>
      </FormFrame>
    );
  }

  return (
    <FormFrame title="Propose payout address" aside={aside} below={<Link to={CA.payouts} className="btn-link text-meta">All payout addresses</Link>}>
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
        <Field label="Payee">
          <select className="select" value={partySlug} onChange={(e) => setPartySlug(e.target.value)} disabled={!customers.data || m.isPending}>
            <option value="">{customers.data ? 'Choose a customer…' : customers.isError ? 'Not read' : 'Reading…'}</option>
            {(customers.data?.customers ?? []).map((c) => <option key={c.slug} value={c.slug}>{c.name}{c.role === 'merchant' ? ' · merchant' : ''}</option>)}
          </select>
          {customers.isError && !isCustodySignin(customers.error) && (
            <StateLine tone="bad" className="mt-1" more={textOf(customers.error)} action={<button type="button" className="btn-link text-meta" onClick={() => void customers.refetch()}>Try again</button>}>
              Couldn&rsquo;t read the customers.
            </StateLine>
          )}
        </Field>
        <Field label="Network"><p className="py-2 text-body">TRC20</p></Field>
        <Field label="Address">
          <input className="input font-mono" placeholder="Enter the full address" value={address} spellCheck={false} autoComplete="off" disabled={m.isPending} onChange={(e) => setAddress(e.target.value)} />
          {!shapeOk && (
            <StateLine tone="bad" className="mt-1" more="A TRON address is 34 characters and starts with T. Copy the whole address from its source.">
              {!trimmed.startsWith('T') ? 'Does not start with T.' : trimmed.length !== 34 ? `${trimmed.length} characters — a TRON address has 34.` : 'Has a character TRON never uses (0, O, I, l).'}
            </StateLine>
          )}
        </Field>
        <Field label="Label (optional)">
          <input className="input" placeholder="Settlement wallet" value={label} disabled={m.isPending} onChange={(e) => setLabel(e.target.value)} />
        </Field>
      </div>

      {reviewing && ready && (
        <div className="rounded-lg border border-stroke bg-panel-2 px-4">
          <Fact label="Payee">{payee!.name}{payee!.role === 'merchant' ? ' · merchant' : ''}</Fact>
          <Fact label="Network">TRC20</Fact>
          <Fact label="Address"><span className="break-all font-mono">{trimmed}</span></Fact>
          {label.trim() && <Fact label="Label">{label.trim()}</Fact>}
        </div>
      )}
      {m.isError && <ServerLine error={m.error} />}

      <FormFoot note={reviewing && ready ? 'Check the address against its source' : 'Payable only after the owner and the test'}>
        {reviewing && ready && <button type="button" className={QUIET} disabled={m.isPending} onClick={() => setReviewing(false)}>Edit</button>}
        {reviewing && ready ? (
          <button type="button" className={PRIMARY} disabled={!ready || m.isPending} onClick={() => m.mutate({ partySlug, payeeName: payee!.name, address: trimmed, label: label.trim() })}>{m.isPending ? 'Proposing…' : 'Propose'}</button>
        ) : (
          <button type="button" className={PRIMARY} disabled={!ready} onClick={() => setReviewing(true)}>Review proposal</button>
        )}
      </FormFoot>
    </FormFrame>
  );
}
